import React, { useState } from 'react';
import './CalculatorPage.css';

function NumerologyReportGenerator() {
  const [name, setName] = useState('');
  const [dob, setDob] = useState('');
  const [report, setReport] = useState(null);
  const [error, setError] = useState('');

  const generateReport = (inputName, dateString) => {
    // Placeholder for Numerology Report Generation logic
    // This is a highly simplified example. A real report would be extensive.
    if (!inputName || !dateString) return null;

    const lifePath = calculateLifePath(dateString);
    const expression = calculateExpression(inputName);
    const soulUrge = calculateSoulUrge(inputName);

    if (lifePath === null) {
      return "Invalid Date of Birth.";
    }

    return {
      lifePath: lifePath,
      expression: expression,
      soulUrge: soulUrge,
      summary: `This is a sample numerology report for ${inputName} born on ${dateString}. Your Life Path Number is ${lifePath}, Expression Number is ${expression}, and Soul Urge Number is ${soulUrge}. A detailed report would include in-depth explanations of each number and their interactions.`
    };
  };

  const calculateLifePath = (dateString) => {
    const digitsOnly = dateString.replace(/\D/g, '');
    if (digitsOnly.length !== 8) return null;

    let sum = 0;
    for (let i = 0; i < digitsOnly.length; i++) {
      sum += parseInt(digitsOnly[i], 10);
    }

    while (sum > 9 && sum !== 11 && sum !== 22 && sum !== 33) {
      sum = sum.toString().split('').reduce((s, d) => s + parseInt(d, 10), 0);
    }
    return sum;
  };

  const calculateExpression = (inputName) => {
    let sum = 0;
    const nameMap = {
      'A': 1, 'J': 1, 'S': 1,
      'B': 2, 'K': 2, 'T': 2,
      'C': 3, 'L': 3, 'U': 3,
      'D': 4, 'M': 4, 'V': 4,
      'E': 5, 'N': 5, 'W': 5,
      'F': 6, 'O': 6, 'X': 6,
      'G': 7, 'P': 7, 'Y': 7,
      'H': 8, 'Q': 8, 'Z': 8,
      'I': 9, 'R': 9
    };

    for (let i = 0; i < inputName.length; i++) {
      const char = inputName[i].toUpperCase();
      if (nameMap[char]) {
        sum += nameMap[char];
      }
    }

    while (sum > 9 && sum !== 11 && sum !== 22) {
      sum = sum.toString().split('').reduce((s, d) => s + parseInt(d, 10), 0);
    }
    return sum;
  };

  const calculateSoulUrge = (inputName) => {
    let sum = 0;
    const vowelMap = {
      'A': 1, 'E': 5, 'I': 9, 'O': 6, 'U': 3,
    };

    for (let i = 0; i < inputName.length; i++) {
      const char = inputName[i].toUpperCase();
      if (vowelMap[char]) {
        sum += vowelMap[char];
      }
    }

    while (sum > 9 && sum !== 11 && sum !== 22) {
      sum = sum.toString().split('').reduce((s, d) => s + parseInt(d, 10), 0);
    }
    return sum;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setError('');
    setReport(null);

    if (!name || !dob) {
      setError('Please enter both your name and date of birth.');
      return;
    }

    const dateRegex = /^\d{2}\/\d{2}\/\d{4}$/;
    if (!dateRegex.test(dob)) {
      setError('Please enter a valid date in DD/MM/YYYY format.');
      return;
    }

    const generated = generateReport(name, dob);
    if (generated === null) {
      setError('Invalid input. Please try again.');
    } else {
      setReport(generated);
    }
  };

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Numerology Report Generator",
    "description": "Generate a personalized numerology report based on your name and birth date.",
    "url": "https://www.yourwebsite.com/numerology-report-generator",
    "potentialAction": {
      "@type": "GenerateAction",
      "name": "Generate Numerology Report",
      "target": {
        "@type": "EntryPoint",
        "urlTemplate": "https://www.yourwebsite.com/numerology-report-generator?name={name}&dob={dob}",
        "actionPlatform": [
          "http://schema.org/DesktopWebPlatform",
          "http://schema.org/MobileWebPlatform"
        ]
      },
      "query-input": [
        "required name=name",
        "required name=dob"
      ]
    }
  };

  return (
    <div className="calculator-page container my-8">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }}
      />
      <h1 className="text-center text-primary">Numerology Report Generator</h1>
      <p className="text-center">Generate a personalized numerology report based on your name and birth date.</p>

      <form onSubmit={handleSubmit} className="calculator-form shadow-md rounded-lg p-6">
        <div className="form-group">
          <label htmlFor="name">Your Full Name:</label>
          <input
            type="text"
            id="name"
            className="form-control"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g., John Doe"
          />
        </div>
        <div className="form-group">
          <label htmlFor="dob">Date of Birth (DD/MM/YYYY):</label>
          <input
            type="text"
            id="dob"
            className="form-control"
            value={dob}
            onChange={(e) => setDob(e.target.value)}
            placeholder="e.g., 01/01/1990"
            maxLength="10"
          />
        </div>
        {error && <p className="error-message">{error}</p>}
        <button type="submit" className="btn btn-primary">Generate Report</button>
      </form>

      {report !== null && (
        <div className="result-section text-center my-8 p-6 shadow-md rounded-lg">
          <h2>Numerology Report Summary:</h2>
          <p className="result-explanation">{report.summary}</p>
          {/* In a real app, you'd display a more detailed, formatted report here */}
          <p>Life Path Number: {report.lifePath}</p>
          <p>Expression Number: {report.expression}</p>
          <p>Soul Urge Number: {report.soulUrge}</p>
          <p><em>For a full PDF report, please contact us.</em></p>
        </div>
      )}
    </div>
  );
}

export default NumerologyReportGenerator;
